"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  eyebrow: string;
  value: string;
  numericTarget: number;
  prefix: string;
  suffix: string;
  decimals: number;
  isText: boolean;
  description: string;
  isAmber: boolean;
};
const stats: Stat[] = [{
  eyebrow: "DOŚWIADCZENIE",
  value: "1047+",
  numericTarget: 1047,
  prefix: "",
  suffix: "+",
  decimals: 0,
  isText: false,
  description: "Metrów kwadratowych zabezpieczonych powierzchni dachowych",
  isAmber: false
}, {
  eyebrow: "SKUTECZNOŚĆ",
  value: "100%",
  numericTarget: 100,
  prefix: "",
  suffix: "%",
  decimals: 0,
  isText: false,
  description: "Szczelność powłok bezspoinowych po aplikacji",
  isAmber: true
}, {
  eyebrow: "SATYSFAKCJA",
  value: "4.9",
  numericTarget: 4.9,
  prefix: "",
  suffix: " \u2605",
  decimals: 1,
  isText: false,
  description: "Średnia ocena naszych realizacji u klientów",
  isAmber: false
}, {
  eyebrow: "TRWAŁOŚĆ",
  value: "25",
  numericTarget: 25,
  prefix: "",
  suffix: " LAT",
  decimals: 0,
  isText: false,
  description: "Gwarantowana odporność na warunki atmosferyczne",
  isAmber: false
}];
const tickerItems = ["HYDROIZOLACJA", "MEMBRANY NATRYSKOWE", "RENOWACJA DACHÓW", "TECHNOLOGIA UV", "BEZSPOINOWE SYSTEMY", "CERTYFIKOWANA JAKOŚĆ"];
function CountUp({
  stat,
  start
}: {
  stat: Stat;
  start: boolean;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start || stat.isText) return;
    const duration = 2000;
    const startTime = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(stat.numericTarget * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setValue(stat.numericTarget);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, stat]);
  if (stat.isText) {
    return <span>{stat.value}</span>;
  }
  const formatted = stat.decimals === 0 ? Math.floor(value).toLocaleString("pl-PL") : value.toFixed(stat.decimals);
  return <span className="!text-7xl">
      {stat.prefix}
      {formatted}
      {stat.suffix}
    </span>;
}
export default function HydroroofStats() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.3
  });
  return <section data-section-id="820" id="hydroroof-w-liczbach" className="bg-background text-foreground relative overflow-hidden py-24">
      <div className="absolute left-0 top-0 h-full w-[3px] bg-primary" />

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-16 flex flex-col gap-4">
          <span className="font-mono text-xs tracking-[0.2em] text-foreground/60">
            05 / DANE TECHNICZNE
          </span>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            HydroRoof w liczbach.
          </h2>
        </div>

        <div ref={ref} className="grid grid-cols-1 gap-y-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-0">
          {stats.map((stat, i) => <div data-index={i} key={i} className={"relative px-0 sm:px-6 lg:px-8 " + (i > 0 ? "lg:border-l lg:border-foreground/15 " + (i % 2 === 0 ? "sm:border-l-0" : "sm:border-l sm:border-foreground/15") : "")}>
              <div className="flex flex-col gap-4">
                <span className="font-mono text-[11px] font-medium tracking-[0.2em] text-foreground/60">
                  {stat.eyebrow}
                </span>

                <div className={"font-default font-extrabold leading-[0.9] tracking-[-0.04em] " + "text-[88px] sm:text-[96px] lg:text-[112px] xl:text-[128px] " + (stat.isAmber ? "text-primary" : "text-foreground")}>
                  <CountUp stat={stat} start={isVisible} />
                </div>

                <p className="text-sm leading-snug text-foreground/70 max-w-[240px]">
                  {stat.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>

      <div className="mt-24 border-t border-foreground/15 py-5 overflow-hidden">
        <div className="flex whitespace-nowrap animate-[ticker_30s_linear_infinite]">
          {[0, 1].map(dup => <div data-index={dup} key={dup} className="flex shrink-0 items-center">
              {tickerItems.map((item, j) => <div data-index={j} key={j} className="flex items-center px-6 font-mono text-xs tracking-[0.2em] text-foreground/70">
                  <span>{item}</span>
                  <span className="ml-6 text-primary">&#9679;</span>
                </div>)}
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}